import {
  DeliverableMagazine,
  Magazine,
  MagazineMeta,
  SubscribingHistory,
} from '../entity/magazine'
import * as Rx from 'rxjs'
import { ApiClient } from './util'
import { environment } from '../../env/environment'
import {
  GlobalNgWord,
  GlobalNgWordList,
  LiveViewerCount,
  LiveViewHistoryList,
  LiveThumbnailPathList,
  Live,
  LiveUpdateRequest,
  LiveThumbnail,
  LiveThumbnailCreateRequest,
  LiveMonitoring,
  LiveListRes,
  LiveList,
  WatchData,
  CreatorListRes,
  CreatorList,
  DeliverableCreatorListRes,
  DeliverableCreator,
  MaintenanceUpdateRequest,
  Maintenance503Res,
  LiveAddRequest,
  LiveAddResponse,
  ArchiveCreateRequest,
  ArchiveAddResponse,
  createLiveUrlResponse,
  LiveConvertToHlsResponse,
} from '../entity/live'
import { Rtmp } from '../entity/room'
import { LiveUserAuthority } from '../entity/live-user-authority'
import { MagliveUser } from '../entity/user'
import { Mag2UserProfile } from '../entity/mag2-user'
import {
  MultipartUploadCreateResponse,
  MultipartUploadPreSignedUrlCreateResponse,
  MultipartUploadPreSignedUrl,
} from '../entity/multipart-upload'
import { catchError, map } from 'rxjs/operators'
import { AjaxResponse } from 'rxjs/ajax'
import { ConfigInfo, ConfigUpdateRequest } from '../entity/remote-config'
import { CsvDataResponse } from '../entity/csv'
import {
  PointBalance,
  PointPlanRequest,
  PointPlansGetRequest,
  PointPlansGetResponse,
  GiftPointHistoryGetRequest,
  GiftPointHistoriesGetResponse,
  PointHistoriesGetRequest,
  PointHistoriesGetResponse,
} from '../entity/manage-point'
import {
  GiftPointHistories,
  GiftPointHistory,
  PointHistoryPeriod,
  PointPlansRes,
  PointPlan,
  StripeReq,
  StripeRes,
  SendGiftRequest,
} from '../entity/gift-point'
import {
  RevenueMonthsRes,
  RevenueMonth,
  RevenueHistoriesForMonth,
} from '../entity/revenue'
import { LiveTransfers, Transfers } from '../entity/transfer'
import { OrderHistoriesResponse, OrderHistoriesRequest } from '../entity/order'
import {
  GiftPostRequest,
  GiftPutRequest,
  GiftIconPostResponse,
  GiftAnimationPostResponse,
  GiftIconPutResponse,
  GiftAnimationPutResponse,
} from '../entity/gift'

/**
 * maglive-server の API を呼び出す API Client.
 */
export class MagliveApiClient {
  static getUserIconURL(userId: number, size: number = 128): string {
    return `${environment.baseURL.magliveIcon}public-api/v2/users/${userId}/icon?d=${size}x${size}`
  }

  // TODO: isLatestPreviewThumbnailは新基盤から使用されなくなるのでisAwsModeをこのメソッドの引数に入れる等して分岐させる必要がある。
  static getLiveThumbnailURL(
    live: Live,
    size: '640x360' | '960x540' = '640x360'
  ): string {
    return live.thumbnailURL && live.isLatestPreviewThumbnail === true
      ? live.thumbnailURL + `?${new Date().getTime()}`
      : live.thumbnailURL
      ? live.thumbnailURL + `?d=${size}`
      : environment.baseURL.magliveWeb + 'assets/images/thumbnail.png'
  }

  static getMagazine(
    magazineId: number
  ): Rx.Observable<Magazine | 'NOT_FOUND'> {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `public-api/v2/magazines/${magazineId}`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<Magazine>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else {
          throw e
        }
      })
    )
  }

  static getAuthority(): Rx.Observable<LiveUserAuthority | 'NOT_LOGIN' | any> {
    const apiPath = `api/v2/authorities`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveUserAuthority>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 503) {
          return Rx.of(e.response as Maintenance503Res)
        } else if (e.status === 401) {
          return Rx.of('NOT_LOGIN' as 'NOT_LOGIN')
        } else {
          throw e
        }
      })
    )
  }

  static getLiveViewerCount(
    liveId: string
  ): Rx.Observable<LiveViewerCount | 'DISABLED'> {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `public-api/v2/lives/${liveId}/viewers/count`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveViewerCount>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('DISABLED' as 'DISABLED')
        } else {
          throw e
        }
      })
    )
  }

  static getLiveViewHistoryList(
    liveId: string
  ): Rx.Observable<LiveViewHistoryList | 'NOT_FOUND'> {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `api/v2/lives/${liveId}/view-history`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveViewHistoryList>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else {
          throw e
        }
      })
    )
  }

  static listSuspendedUser(): Rx.Observable<MagliveUser[] | 'NOT_AUTHORIZED'> {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/users/suspended`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<MagliveUser[]>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static addSuspendUser(
    userId: number
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/users/${userId}/suspend`,
      {}
    ).pipe(
      map((_) => {
        return null
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static removeSuspendUser(
    userId: number
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.deleteJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/users/${userId}/suspend`
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static listSuspendedMagazine(): Rx.Observable<
    MagazineMeta[] | 'NOT_AUTHORIZED'
  > {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/magazines/suspended`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<MagazineMeta[]>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static addSuspendMagazine(
    magazineId: number
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/magazines/${magazineId}/suspend`,
      {}
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static removeSuspendMagazine(
    magazineId: number
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.deleteJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/magazines/${magazineId}/suspend`
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static getUserProfile(
    userId: number
  ): Rx.Observable<Mag2UserProfile | 'NOT_AUTHORIZED' | 'NOT_FOUND'> {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `public-api/v2/users/${userId}/profile`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<Mag2UserProfile>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else {
          throw e
        }
      })
    )
  }

  static editMyProfile(
    nickname: string
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      `api/v2/users/my/profile`,
      { nickname }
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static removeUserProfile(
    userId: number
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.deleteJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/users/${userId}/profile`
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static removeUserIcon(
    userId: number
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.deleteJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/users/${userId}/icon`
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static deleteComment(
    liveId: string,
    commentId: string
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.deleteJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/lives/${liveId}/comments/${commentId}`
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static listGlobalNgWords(): Rx.Observable<GlobalNgWord[] | 'NOT_AUTHORIZED'> {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/ng-words`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<GlobalNgWordList>
        return resp.data.items
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static addGlobalNgWord(word: string): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/ng-words`,
      { word: word }
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 400 || e.status == 409) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static deleteGlobalNgWord(
    globalNgWordId: string
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    return ApiClient.deleteJson(
      environment.baseURL.magliveAPI,
      `api/v2/manage/ng-words/${globalNgWordId}`
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static getMonitoringUrl(
    liveId: string
  ): Rx.Observable<LiveMonitoring | 'NOT_AUTHORIZED'> {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `api/v3/manage/lives/${liveId}/monitoring`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveMonitoring>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  static listLiveThumbnailPaths(magazineId: string): Rx.Observable<string[]> {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `public-api/v2/magazines/${magazineId}/live-thumbnails`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveThumbnailPathList>
        return resp.data.paths
      }),
      catchError((e) => {
        throw e
      })
    )
  }

  static followMagazine(
    magazineId: string,
    token: string
  ): Rx.Observable<void> {
    return ApiClient.putJson(
      environment.baseURL.magliveAPI,
      `api/v2/magazines/${magazineId}/follows`,
      { following: true, token }
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        throw e
      })
    )
  }

  static unfollowMagazine(
    magazineId: string,
    token: string
  ): Rx.Observable<void> {
    return ApiClient.putJson(
      environment.baseURL.magliveAPI,
      `api/v2/magazines/${magazineId}/follows`,
      { following: false, token }
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        throw e
      })
    )
  }

  static addComment(
    liveId: string,
    message: string
  ): Rx.Observable<
    | void
    | 'NOT_LOGIN'
    | 'INSUFFICIENT_LIVE_CONDITION'
    | 'ENTITY_TOO_LARGE'
    | 'UNACCEPTABLE_CONTENT'
  > {
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      `api/v2/lives/${liveId}/comments`,
      { message }
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 401 && e.response.code === 'NOT_LOGIN') {
          return Rx.of('NOT_LOGIN' as 'NOT_LOGIN')
        }
        if (
          e.status === 400 &&
          e.response.code === 'INSUFFICIENT_LIVE_CONDITION'
        ) {
          return Rx.of(
            'INSUFFICIENT_LIVE_CONDITION' as 'INSUFFICIENT_LIVE_CONDITION'
          )
        }
        if (e.status === 400 && e.response.code === 'ENTITY_TOO_LARGE') {
          return Rx.of('ENTITY_TOO_LARGE' as 'ENTITY_TOO_LARGE')
        }
        if (e.status === 400 && e.response.code === 'UNACCEPTABLE_CONTENT') {
          return Rx.of('UNACCEPTABLE_CONTENT' as 'UNACCEPTABLE_CONTENT')
        }
        throw e
      })
    )
  }

  static postLike(
    liveId: string,
    count: number
  ): Rx.Observable<void | 'NOT_LOGIN' | 'INSUFFICIENT_LIVE_CONDITION'> {
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      `api/v2/lives/${liveId}/reactions`,
      { items: [{ kind: 'GOOD', count }] }
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 401 && e.response.code === 'NOT_LOGIN') {
          return Rx.of('NOT_LOGIN' as 'NOT_LOGIN')
        }
        if (
          e.status === 400 &&
          e.response.code === 'INSUFFICIENT_LIVE_CONDITION'
        ) {
          return Rx.of(
            'INSUFFICIENT_LIVE_CONDITION' as 'INSUFFICIENT_LIVE_CONDITION'
          )
        }
        throw e
      })
    )
  }

  static getSubscribingHistories(
    magazineId?: number
  ): Rx.Observable<SubscribingHistory[] | 'NOT_AUTHORIZED' | 'NOT_LOGIN'> {
    const apiPath = magazineId
      ? `api/v2/magazines/${magazineId}/subscribing/histories`
      : 'api/v2/magazines/subscribing/histories'
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<SubscribingHistory[]>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 401 && e.response.code === 'NOT_LOGIN') {
          return Rx.of('NOT_LOGIN' as 'NOT_LOGIN')
        }
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        }
        throw e
      })
    )
  }

  // 配信一覧取得API
  static getLiveHistories(
    lastDocumentId: string
  ): Rx.Observable<LiveListRes | 'NOT_FOUND'> {
    let apiPath = 'api/v3/manage/lives'
    if (lastDocumentId != null) {
      apiPath = apiPath += `?lastDocumentId=${lastDocumentId}`
    }
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveListRes>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // magazineId で配信一覧取得API
  static getLivesByMagazineId(
    lastDocumentId: string,
    magazineId: number
  ): Rx.Observable<LiveListRes | 'NOT_FOUND'> {
    let apiPath = `api/v3/manage/lives?magazineId=${magazineId}`
    if (lastDocumentId != null) {
      apiPath = apiPath += `&lastDocumentId=${lastDocumentId}`
    }
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveListRes>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // 配信情報取得API (入室API) (視聴者側)
  static getWatchData(
    liveId: number | string
  ): Rx.Observable<WatchData | 'NOT_FOUND'> {
    const apiPath = `api/v3/lives/${liveId}/broadcast?client=web`
    // return ApiClient.getJsonWithHeader('http://localhost:7077/', apiPath).pipe( // APIモック
    return ApiClient.getJsonWithHeader(
      environment.baseURL.magliveAPI,
      apiPath
    ).pipe(
      map((x: AjaxResponse) => {
        const resp = x.response as MagliveApiResponse<WatchData>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // 配信情報取得API (入室API) (管理側)
  static getMngWatchData(
    liveId: number | string
  ): Rx.Observable<WatchData | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/lives/${liveId}/broadcast`
    // return ApiClient.getJson('http://localhost:7077/', apiPath).pipe( // APIモック
    return ApiClient.getJsonWithHeader(
      environment.baseURL.magliveAPI,
      apiPath
    ).pipe(
      map((x: AjaxResponse) => {
        const resp = x.response as MagliveApiResponse<WatchData>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  /*
    Enter the distribution as a specific user
    GET /api/v3/manage/lives/:liveId/users/:userId/broadcast
  */
  static getBroadcastByUserId(
    liveId: number | string,
    userId: number
  ): Rx.Observable<WatchData | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/lives/${liveId}/users/${userId}/broadcast`
    // return ApiClient.getJson('http://localhost:7077/', apiPath).pipe( // APIモック
    return ApiClient.getJsonWithHeader(
      environment.baseURL.magliveAPI,
      apiPath
    ).pipe(
      map((x: AjaxResponse) => {
        const resp = x.response as MagliveApiResponse<WatchData>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // 配信者情報取得API (管理側)
  static getCreatorList(): Rx.Observable<CreatorList[] | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/creators_history`
    // return ApiClient.getJson('http://localhost:7077/', apiPath).pipe( // APIモック
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<CreatorListRes>
        return resp.data.histories
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // 配信可能な配信者情報取得API (管理側)
  // 代理予約作成、ライブ編集項目のセレクトボックスで使用
  static getDeliverableCreatorList(): Rx.Observable<
    DeliverableCreator[] | 'NOT_FOUND'
  > {
    const apiPath = `api/v3/manage/creators`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<DeliverableCreatorListRes>
        return resp.data.users
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // メンテナンス情報登録API
  static updateMaintenanceInfo(params: MaintenanceUpdateRequest) {
    const apiPath = `api/v3/manage/maintenance`
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      apiPath,
      params
    ).pipe(
      map((x) => {
        return Rx.of(x)
      }),
      catchError((e) => {
        console.error(e)
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  // 参照データ登録API
  static updateConfigInfo(params: ConfigUpdateRequest) {
    const apiPath = `api/v3/manage/config`
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      apiPath,
      params
    ).pipe(
      map((x) => {
        return Rx.of(x)
      }),
      catchError((e) => {
        console.error(e)
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  // 配信情報更新API (管理側)
  static updateLiveInfo(liveId: number | string, params: LiveUpdateRequest) {
    const apiPath = `api/v3/manage/lives/${liveId}`
    return ApiClient.putJson(
      environment.baseURL.magliveAPI,
      apiPath,
      params
    ).pipe(
      map((x) => {
        return Rx.of(x)
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  // サムネイル作成API (配信者側)
  static createLiveThumbnail(
    magazineId: number,
    data: any
  ): Rx.Observable<LiveThumbnail | 'NOT_AUTHORIZED'> {
    const apiPath = `api/v2/magazines/${magazineId}/live-thumbnails`
    return ApiClient.postFile(
      environment.baseURL.magliveAPI,
      apiPath,
      data
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveThumbnail>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  // サムネイル削除API (配信者側)
  static deleteLiveThumbnail(
    magazineId: number,
    path: string
  ): Rx.Observable<void | 'NOT_AUTHORIZED'> {
    const apiPath = `api/v2/magazines/${magazineId}/live-thumbnails/${path}`
    return ApiClient.deleteJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  // 固定コメント作成API (管理側)
  static createCreatorComment(liveId: string | number, data: any) {
    const apiPath = `api/v3/manage/lives/${liveId}/fixed_comments`
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      apiPath,
      data
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  // NOTE ライブ削除機能リリース時にコメント解除
  // static deleteLive(liveId: string): Rx.Observable<void | "NOT_AUTHORIZED"> {
  //   return ApiClient.deleteJson(environment.baseURL.magliveAPI, `api/v3/manage/lives/${liveId}`).pipe(
  //     map(_ => {
  //       return
  //     }),
  //     catchError(e => {
  //       console.error(e)
  //       if (e.status === 400) {
  //         return Rx.of("NOT_AUTHORIZED" as "NOT_AUTHORIZED");
  //       } else {
  //         throw e;
  //       }
  //     })
  //   )
  // }

  // 代理予約作成
  static addLiveInfo(
    params: LiveAddRequest
  ): Rx.Observable<LiveAddResponse | 'NOT_AUTHORIZED'> {
    const apiPath = `api/v3/manage/lives`
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      apiPath,
      params
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveAddResponse>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  //アーカイブ作成
  static addArchiveInfo(
    params: ArchiveCreateRequest
  ): Rx.Observable<ArchiveAddResponse | 'NOT_AUTHORIZED'> {
    const apiPath = `api/v3/manage/lives/archive`
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      apiPath,
      params
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<ArchiveAddResponse>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  // アーカイブアップロードステータス更新
  static updateArchiveUploadStatus(
    liveId: string,
    status: string
  ): Rx.Observable<void> {
    return ApiClient.putJson(
      environment.baseURL.magliveAPI,
      `api/v3/manage/lives/${liveId}/archive/upload-status`,
      { uploadStatus: status }
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        throw e
      })
    )
  }

  static updateArchiveUploadStatusByFetch(liveId: string, status: string) {
    return ApiClient.putJsonFetch(
      environment.baseURL.magliveAPI,
      `api/v3/manage/lives/${liveId}/archive/upload-status`,
      { uploadStatus: status }
    )
  }

  // 配信情報更新API (管理側)
  static updateArchive(liveId: number | string, params: LiveUpdateRequest) {
    const apiPath = `api/v3/manage/lives/${liveId}/archive`
    return ApiClient.putJson(
      environment.baseURL.magliveAPI,
      apiPath,
      params
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  static getRtmpInfo(
    liveId: string
  ): Rx.Observable<Rtmp | 'NOT_AUTHORIZED' | 'NOT_FOUND'> {
    return ApiClient.getJson(
      environment.baseURL.magliveAPI,
      `api/v3/lives/${liveId}/room`
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<Rtmp>
        return resp.data
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else {
          throw e
        }
      })
    )
  }

  // 配信可能なマガジンとクリエイターを取得
  // 代理予約作成のセレクトボックスで使用
  static getMagazineList(): Rx.Observable<DeliverableMagazine[] | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/magazines`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<DeliverableMagazine[]>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // ライブコメントCSVダウンロード
  static exportCsvLiveComment(
    liveId: string | number
  ): Rx.Observable<CsvDataResponse | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/export_csv/${liveId}`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<CsvDataResponse>
        return resp.data
      }),
      catchError((e) => {
        console.log(e)
        if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else {
          throw e
        }
      })
    )
  }

  // ポイントプラン取得 (管理側)
  static getPointPlansForManage(
    params: PointPlansGetRequest
  ): Rx.Observable<PointPlansGetResponse | 'NOT_FOUND'> {
    let apiPath = `api/v3/manage/point-plans?deviceType=${params.deviceType}&isSalesEndOnly=${params.isSalesEndOnly}`
    if (params.lastDocumentId) {
      apiPath = apiPath += `&lastDocumentId=${params.lastDocumentId}`
    }
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<PointPlansGetResponse>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // [ポイント管理] ポイント履歴の取得
  static getGiftPointHistories(
    yearMonth: string
  ): Rx.Observable<GiftPointHistory[] | 'NOT_FOUND'> {
    const apiPath = `api/v3/users/point-histories?yearMonth=${yearMonth}`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<GiftPointHistories>
        return response.data.items
      }),
      catchError((e) => {
        console.log(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // ポイントプラン登録 (管理側)
  static addPointPlan(
    params: PointPlanRequest
  ): Rx.Observable<any | 'NOT_ADMIN' | 'VALIDATION_ERROR'> {
    const apiPath = `api/v3/manage/point-plans`
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      apiPath,
      params
    ).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<any>
        console.log(response)
        return response
      }),
      catchError((e) => {
        if (e.status == 400) {
          if (e.code === 'NOT_ADMIN') {
            return Rx.of('NOT_ADMIN' as 'NOT_ADMIN')
          } else {
            return Rx.of('VALIDATION_ERROR' as 'VALIDATION_ERROR')
          }
        } else {
          throw e
        }
      })
    )
  }

  // ポイントプラン削除 (管理側)
  static deletePointPlan(
    pointPlanId: string
  ): Rx.Observable<any | 'NOT_AUTHORIZED'> {
    const apiPath = `api/v3/manage/point-plans/${pointPlanId}`
    return ApiClient.deleteJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<any>
        console.log(response)
        return response
      }),
      catchError((e) => {
        if (e.status === 400) {
          return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
        } else {
          throw e
        }
      })
    )
  }

  // [ポイント管理] ポイント履歴の月一覧取得
  static getGiftPointHistoryPeriod(): Rx.Observable<
    PointHistoryPeriod | 'NOT_FOUND'
  > {
    const apiPath = `api/v3/users/point-history-period`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<PointHistoryPeriod>
        return response.data
      }),
      catchError((e) => {
        console.log(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // ポイントプラン編集 (管理側)
  static updatePointPlan(
    params: PointPlanRequest,
    planId: string
  ): Rx.Observable<any | 'NOT_ADMIN' | 'VALIDATION_ERROR'> {
    const apiPath = `api/v3/manage/point-plans/${planId}`
    return ApiClient.putJson(
      environment.baseURL.magliveAPI,
      apiPath,
      params
    ).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<any>
        return response
      }),
      catchError((e) => {
        if (e.status === 400) {
          if (e.code == 'NOT_ADMIN') {
            return Rx.of('NOT_ADMIN' as 'NOT_ADMIN')
          } else {
            return Rx.of('VALIDATION_ERROR' as 'VALIDATION_ERROR')
          }
        } else {
          throw e
        }
      })
    )
  }

  // [ポイント管理] ポイントプラン一覧取得
  static getPointPlans(): Rx.Observable<PointPlan[] | 'NOT_FOUND'> {
    const apiPath = 'public-api/v3/point-plans'
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<PointPlansRes>
        return response.data.items
      }),
      catchError((e) => {
        console.log(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // [収益管理] 収益履歴の月一覧取得
  static getRevenueMonthsHistory(
    magazineId: number
  ): Rx.Observable<RevenueMonth[] | 'NOT_FOUND'> {
    const apiPath = `api/v3/magazines/${magazineId}/sale-period`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<RevenueMonthsRes>
        return response.data.items
      }),
      catchError((e) => {
        console.log(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // ポイント残高取得 (管理側)
  static fetchPointBalances(
    yearMonth: string
  ): Rx.Observable<PointBalance | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/point-balances?yearMonth=${yearMonth}`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<PointBalance>
        return response.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // [収益管理] 指定月の収益履歴取得
  static getRevenueHistoriesForMonth(
    magazineId: number,
    yearMonth: string
  ): Rx.Observable<RevenueHistoriesForMonth | 'NOT_FOUND'> {
    const apiPath = `api/v3/magazines/${magazineId}/monthly-sales?yearMonth=${yearMonth}`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<RevenueHistoriesForMonth>
        return response.data
      }),
      catchError((e) => {
        console.log(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // ポイント履歴取得 (管理側)
  static getPointHistories(
    params: PointHistoriesGetRequest
  ): Rx.Observable<PointHistoriesGetResponse | 'NOT_FOUND'> {
    let apiPath = 'api/v3/manage/point-histories?'
    if (params.id) {
      apiPath = apiPath += `id=${params.id}&`
    }
    if (params.userId) {
      apiPath = apiPath += `userId=${params.userId}&`
    }
    if (params.createdAtStart) {
      apiPath = apiPath += `createdAtStart=${params.createdAtStart}&`
    }
    if (params.createdAtEnd) {
      apiPath = apiPath += `createdAtEnd=${params.createdAtEnd}&`
    }
    if (params.pointHistoryKind) {
      apiPath = apiPath += `pointHistoryKind=${params.pointHistoryKind}&`
    }
    if (params.lastDocumentId) {
      apiPath = apiPath += `lastDocumentId=${params.lastDocumentId}`
    }

    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<PointHistoriesGetResponse>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // ポイント履歴をcsvファイルにエクスポート (管理側)
  static exportPointHistories(
    params: PointHistoriesGetRequest
  ): Rx.Observable<CsvDataResponse | 'NOT_FOUND' | 'VALIDATION_ERROR'> {
    let apiPath = 'api/v3/manage/point-histories-csv?'
    if (params.id) {
      apiPath = apiPath += `id=${params.id}&`
    }
    if (params.userId) {
      apiPath = apiPath += `userId=${params.userId}&`
    }
    if (params.createdAtStart) {
      apiPath = apiPath += `createdAtStart=${params.createdAtStart}&`
    }
    if (params.createdAtEnd) {
      apiPath = apiPath += `createdAtEnd=${params.createdAtEnd}&`
    }
    if (params.pointHistoryKind) {
      apiPath = apiPath += `pointHistoryKind=${params.pointHistoryKind}&`
    }
    if (params.lastDocumentId) {
      apiPath = apiPath += `lastDocumentId=${params.lastDocumentId}`
    }

    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<CsvDataResponse>
        return resp.data
      }),
      catchError((e) => {
        console.log(e)
        if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else if(e.status === 400) {
          return Rx.of('VALIDATION_ERROR' as 'VALIDATION_ERROR')
        } else {
          throw e
        }
      })
    )
  }

  // ポイント残高更新 (管理側)
  static updatePointBalances(): Rx.Observable<PointBalance | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/point-balances`
    return ApiClient.postJson(environment.baseURL.magliveAPI, apiPath, {}).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<PointBalance>
        return response.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // ライブをmp4形式へ変換する
  static convertLive(roomId: string) {
    const apiPath = `api/v3/manage/rooms/${roomId}/convert`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        return Rx.of(x)
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  // ギフト収益履歴取得 (管理側)
  static getGiftPointHistoriesForManage(
    params: GiftPointHistoryGetRequest
  ): Rx.Observable<GiftPointHistoriesGetResponse | 'NOT_FOUND'> {
    let apiPath = 'api/v3/manage/sales-histories?'
    if (params.id) {
      apiPath = apiPath += `id=${params.id}&`
    }
    if (params.magazineId) {
      apiPath = apiPath += `magazineId=${params.magazineId}&`
    }
    if (params.receiverId) {
      apiPath = apiPath += `receiverId=${params.receiverId}&`
    }
    if (params.senderId) {
      apiPath = apiPath += `senderId=${params.senderId}&`
    }
    if (params.createdAtStart) {
      apiPath = apiPath += `createdAtStart=${params.createdAtStart}&`
    }
    if (params.createdAtEnd) {
      apiPath = apiPath += `createdAtEnd=${params.createdAtEnd}&`
    }
    if (params.lastDocumentId) {
      apiPath = apiPath += `lastDocumentId=${params.lastDocumentId}`
    }

    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<GiftPointHistoriesGetResponse>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // （管理画面）指定月の振込データ一覧取得
  static fetchTransfers(
    yearMonth: string
  ): Rx.Observable<Transfers | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/transfers?yearMonth=${yearMonth}`

    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<Transfers>
        return response.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // （管理画面）指定月の振込データCSVダウンロード
  static exportCsvTransfer(
    yearMonth: string
  ): Rx.Observable<CsvDataResponse | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/transfers-csv/?yearMonth=${yearMonth}`

    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<CsvDataResponse>
        return resp.data
      }),
      catchError((e) => {
        console.log(e)
        if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else {
          throw e
        }
      })
    )
  }

  // （管理画面）ポイント残高CSVダウンロード
  static exportCsvPointBalance(
    yearMonth: string,
    isDaily: boolean
  ): Rx.Observable<CsvDataResponse | 'NOT_FOUND'> {
    let apiPath = `api/v3/manage/point-balances-csv/?yearMonth=${yearMonth}&`
    if (isDaily) {
      apiPath = apiPath += `isDaily=${isDaily}`
    }
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<CsvDataResponse>
        return resp.data
      }),
      catchError((e) => {
        console.log(e)
        if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else {
          throw e
        }
      })
    )
  }

  // （管理画面）ギフト収益履歴CSVダウンロード
  static exportCsvSalesHistory(
    params: GiftPointHistoryGetRequest
  ): Rx.Observable<CsvDataResponse | 'NOT_FOUND'> {
    let apiPath = 'api/v3/manage/sales-histories-csv?'
    if (params.id) {
      apiPath = apiPath += `id=${params.id}&`
    }
    if (params.magazineId) {
      apiPath = apiPath += `magazineId=${params.magazineId}&`
    }
    if (params.receiverId) {
      apiPath = apiPath += `receiverId=${params.receiverId}&`
    }
    if (params.senderId) {
      apiPath = apiPath += `senderId=${params.senderId}&`
    }
    if (params.createdAtStart) {
      apiPath = apiPath += `createdAtStart=${params.createdAtStart}&`
    }
    if (params.createdAtEnd) {
      apiPath = apiPath += `createdAtEnd=${params.createdAtEnd}&`
    }
    if (params.lastDocumentId) {
      apiPath = apiPath += `lastDocumentId=${params.lastDocumentId}`
    }
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<CsvDataResponse>
        return resp.data
      }),
      catchError((e) => {
        console.log(e)
        if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else {
          throw e
        }
      })
    )
  }

  // [ポイント] ポイントチャージ注文
  static postCheckoutSession(
    request: StripeReq
  ): Rx.Observable<StripeRes | 'NOT_FOUND'> {
    const apiPath = `api/v3/orders/get-stripe-checkout-session`
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      apiPath,
      request
    ).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<StripeRes>
        return response.data
      }),
      catchError((e) => {
        console.log(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // ギフト送付
  static sendGift(
    liveId: string,
    request: SendGiftRequest
  ): Rx.Observable<
    | void
    | 'NOT_FOUND'
    | 'NOT_LOGIN'
    | 'INSUFFICIENT_LIVE_CONDITION'
    | 'ENTITY_TOO_LARGE'
    | 'UNACCEPTABLE_CONTENT'
  > {
    const apiPath = `api/v3/lives/${liveId}/gifts`
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      apiPath,
      request
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        if (e.status === 401 && e.response.code === 'NOT_LOGIN') {
          return Rx.of('NOT_LOGIN' as 'NOT_LOGIN')
        }
        if (
          e.status === 400 &&
          e.response.code === 'INSUFFICIENT_LIVE_CONDITION'
        ) {
          return Rx.of(
            'INSUFFICIENT_LIVE_CONDITION' as 'INSUFFICIENT_LIVE_CONDITION'
          )
        }
        if (e.status === 400 && e.response.code === 'ENTITY_TOO_LARGE') {
          return Rx.of('ENTITY_TOO_LARGE' as 'ENTITY_TOO_LARGE')
        }
        if (e.status === 400 && e.response.code === 'UNACCEPTABLE_CONTENT') {
          return Rx.of('UNACCEPTABLE_CONTENT' as 'UNACCEPTABLE_CONTENT')
        }
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // ニックネーム更新
  static putMyProfile(nickname: string): Rx.Observable<void | 'SERVER_ERROR'> {
    const apiPath = `api/v2/users/my/profile`
    return ApiClient.postJson(environment.baseURL.magliveAPI, apiPath, {
      nickname: nickname,
    }).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        console.log(e)
        return Rx.of('SERVER_ERROR' as 'SERVER_ERROR')
      })
    )
  }

  // (管理画面) 注文履歴取得
  static fetchOrderHistories(
    request: OrderHistoriesRequest
  ): Rx.Observable<OrderHistoriesResponse | 'NOT_FOUND'> {
    let apiPath = '/api/v3/manage/orders?'
    if (request.id) apiPath += `id=${request.id}&`
    if (request.userId) apiPath += `userId=${request.userId}&`
    if (request.paymentMethod)
      apiPath += `paymentMethod=${request.paymentMethod}&`
    if (request.pointPlanSerialNumber)
      apiPath += `pointPlanSerialNumber=${request.pointPlanSerialNumber}&`
    if (request.status) apiPath += `status=${request.status}&`
    if (request.createdAtStart)
      apiPath += `createdAtStart=${request.createdAtStart}&`
    if (request.createdAtEnd) apiPath += `createdAtEnd=${request.createdAtEnd}&`
    if (request.lastDocumentId)
      apiPath += `lastDocumentId=${request.lastDocumentId}`

    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<OrderHistoriesResponse>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // (管理画面) 注文履歴をCSV エクスポート
  static exportOrderHistories(
    request: OrderHistoriesRequest
  ): Rx.Observable<CsvDataResponse | 'NOT_FOUND' | 'VALIDATION_ERROR'> {
    let apiPath = '/api/v3/manage/order-histories-csv?'
    if (request.id) apiPath += `id=${request.id}&`
    if (request.userId) apiPath += `userId=${request.userId}&`
    if (request.paymentMethod)
      apiPath += `paymentMethod=${request.paymentMethod}&`
    if (request.pointPlanSerialNumber)
      apiPath += `pointPlanSerialNumber=${request.pointPlanSerialNumber}&`
    if (request.status) apiPath += `status=${request.status}&`
    if (request.createdAtStart)
      apiPath += `createdAtStart=${request.createdAtStart}&`
    if (request.createdAtEnd) apiPath += `createdAtEnd=${request.createdAtEnd}&`
    if (request.lastDocumentId)
      apiPath += `lastDocumentId=${request.lastDocumentId}`

      return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
        map((x) => {
          const resp = x as MagliveApiResponse<CsvDataResponse>
          return resp.data
        }),
        catchError((e) => {
          if (e.status === 404) {
            return Rx.of('NOT_FOUND' as 'NOT_FOUND')
          } else if(e.status === 400) {
            return Rx.of('VALIDATION_ERROR' as 'VALIDATION_ERROR')
          } else {
            throw e
          }
        })
      )
  }

  // mp4に変換したライブをDownloadできる署名URLを発行する
  static createDownloadUrl(
    roomId: string
  ): Rx.Observable<createLiveUrlResponse> {
    const apiPath = `api/v3/manage/rooms/${roomId}/url`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<createLiveUrlResponse>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  // マルチパートアップロード初期化
  static createMultipartUpload(
    liveId: string,
    objectPath: string
  ): Rx.Observable<MultipartUploadCreateResponse> {
    const apiPath = `api/v3/manage/lives/${liveId}/archive/multipart-upload`
    return ApiClient.postJson(environment.baseURL.magliveAPI, apiPath, {
      objectPath: objectPath,
    }).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<MultipartUploadCreateResponse>
        return resp.data
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  // マルチパートアップロードの署名付きURL取得
  static createPreSignedUrls(
    uploadId: string,
    objectPath: string,
    count: number
  ): Rx.Observable<MultipartUploadPreSignedUrl[]> {
    const apiPath = `api/v3/manage/lives/archive/multipart-pre-signed-url`
    return ApiClient.postJson(environment.baseURL.magliveAPI, apiPath, {
      uploadId: uploadId,
      objectPath: objectPath,
      count: count,
    }).pipe(
      map((x) => {
        const resp =
          x as MagliveApiResponse<MultipartUploadPreSignedUrlCreateResponse>
        return resp.data.items
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }

  // 署名付きURLによるアップロード処理
  static uploadPreSignedUrl(
    endPoint: string,
    file: any
  ): Rx.Observable<string> {
    return ApiClient.putJson(endPoint, '', file, {
      'Content-Type': 'multipart/form-data',
    }).pipe(
      map((x) => {
        return (x as AjaxResponse).xhr.getResponseHeader('ETag')
      }),
      catchError((e) => {
        console.error(e)
        throw e
      })
    )
  }
  // マルチパートアップロード終了
  static completeMultipartUpload(
    liveId: string,
    uploadId: string,
    objectPath: string,
    multipartMap: any
  ): Rx.Observable<void> {
    const apiPath = `api/v3/manage/lives/${liveId}/archive/complete-multipart-upload`
    return ApiClient.postJson(environment.baseURL.magliveAPI, apiPath, {
      liveId: liveId,
      uploadId: uploadId,
      objectPath: objectPath,
      multipartMap: multipartMap,
    }).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        throw e
      })
    )
  }

  // アーカイブを変換
  static convertArchive(
    liveId: string,
    objectPath: string,
    fileType: string
  ): Rx.Observable<LiveConvertToHlsResponse> {
    const apiPath = `api/v3/manage/lives/${liveId}/archive/convert`
    return ApiClient.postJson(environment.baseURL.magliveAPI, apiPath, {
      objectPath: objectPath,
      fileType: fileType,
    }).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<LiveConvertToHlsResponse>
        return resp.data
      }),
      catchError((e) => {
        throw e
      })
    )
  }

  // ギフト登録 (管理側)
  static creatGift(
    request: GiftPostRequest
  ): Rx.Observable<void | 'NOT_AUTHORIZED' | 'BAD_REQUEST'> {
    const apiPath = `api/v3/manage/gifts`
    return ApiClient.postJson(
      environment.baseURL.magliveAPI,
      apiPath,
      request
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        const error = JSON.parse(e)
        if (error.status === 400) {
          if (error.code === 'NOT_ADMIN') {
            return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
          } else {
            return Rx.of('BAD_REQUEST' as 'BAD_REQUEST')
          }
        } else {
          throw error
        }
      })
    )
  }

  // ギフト更新 (管理側)
  static updateGift(
    giftId: String,
    request: GiftPutRequest
  ): Rx.Observable<void | 'NOT_AUTHORIZED' | 'BAD_REQUEST'> {
    const apiPath = `api/v3/manage/gifts/${giftId}`
    return ApiClient.putJson(
      environment.baseURL.magliveAPI,
      apiPath,
      request
    ).pipe(
      map((_) => {
        return
      }),
      catchError((e) => {
        const error = JSON.parse(e)
        if (error.status === 400) {
          if (error.code === 'NOT_ADMIN') {
            return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
          } else {
            return Rx.of('BAD_REQUEST' as 'BAD_REQUEST')
          }
        } else {
          throw error
        }
      })
    )
  }

  // ギフトアイコン登録API (配信者側)
  static createGiftIcon(
    data: any
  ): Rx.Observable<GiftIconPostResponse | 'NOT_AUTHORIZED' | 'BAD_REQUEST'> {
    const apiPath = `api/v3/manage/gifts/icon`
    return ApiClient.postFile(
      environment.baseURL.magliveAPI,
      apiPath,
      data
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<GiftIconPostResponse>
        return resp.data
      }),
      catchError((e) => {
        const error = JSON.parse(e)
        if (error.status === 400) {
          if (error.code === 'NOT_ADMIN') {
            return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
          } else {
            return Rx.of('BAD_REQUEST' as 'BAD_REQUEST')
          }
        } else {
          throw error
        }
      })
    )
  }

  // ギフトアニメーション登録API (配信者側)
  static createGiftAnimation(
    data: any
  ): Rx.Observable<
    GiftAnimationPostResponse | 'NOT_AUTHORIZED' | 'BAD_REQUEST'
  > {
    const apiPath = `api/v3/manage/gifts/animation`
    return ApiClient.postFile(
      environment.baseURL.magliveAPI,
      apiPath,
      data
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<GiftAnimationPostResponse>
        return resp.data
      }),
      catchError((e) => {
        const error = JSON.parse(e)
        if (error.status === 400) {
          if (error.code === 'NOT_ADMIN') {
            return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
          } else {
            return Rx.of('BAD_REQUEST' as 'BAD_REQUEST')
          }
        } else {
          throw error
        }
      })
    )
  }

  // ギフトアイコン更新API (配信者側)
  static updateGiftIcon(
    giftId: string,
    data: any
  ): Rx.Observable<GiftIconPutResponse | 'NOT_AUTHORIZED' | 'BAD_REQUEST'> {
    const apiPath = `api/v3/manage/gifts/${giftId}/icon`
    return ApiClient.postFile(
      environment.baseURL.magliveAPI,
      apiPath,
      data
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<GiftIconPutResponse>
        return resp.data
      }),
      catchError((e) => {
        const error = JSON.parse(e)
        if (error.status === 400) {
          if (error.code === 'NOT_ADMIN') {
            return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
          } else {
            return Rx.of('BAD_REQUEST' as 'BAD_REQUEST')
          }
        } else {
          throw error
        }
      })
    )
  }

  // ギフトアニメーション更新API (配信者側)
  static updateGiftAnimation(
    giftId: string,
    data: any
  ): Rx.Observable<
    GiftAnimationPutResponse | 'NOT_AUTHORIZED' | 'BAD_REQUEST'
  > {
    const apiPath = `api/v3/manage/gifts/${giftId}/animation`
    return ApiClient.postFile(
      environment.baseURL.magliveAPI,
      apiPath,
      data
    ).pipe(
      map((x) => {
        const resp = x as MagliveApiResponse<GiftAnimationPutResponse>
        return resp.data
      }),
      catchError((e) => {
        const error = JSON.parse(e)
        if (error.status === 400) {
          if (error.code === 'NOT_ADMIN') {
            return Rx.of('NOT_AUTHORIZED' as 'NOT_AUTHORIZED')
          } else {
            return Rx.of('BAD_REQUEST' as 'BAD_REQUEST')
          }
        } else {
          throw error
        }
      })
    )
  }

  // （管理画面）ライブ単位・指定月の振込データ一覧取得
  static fetchLiveTransfers(
    yearMonth: string,
  ): Rx.Observable<LiveTransfers | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/live-transfers?yearMonth=${yearMonth}`
    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<LiveTransfers>
        console.log(response.data)
        return response.data
      }),
      catchError((e) => {
        console.error(e)
        return Rx.of('NOT_FOUND' as 'NOT_FOUND')
      })
    )
  }

  // （管理画面）ライブ単位・指定月の振込データCSVダウンロード
  static exportCsvLiveTransfer(
    yearMonth: string,
  ): Rx.Observable<CsvDataResponse | 'NOT_FOUND'> {
    const apiPath = `api/v3/manage/live-transfers-csv/?yearMonth=${yearMonth}`

    return ApiClient.getJson(environment.baseURL.magliveAPI, apiPath).pipe(
      map((x) => {
        const response = x as MagliveApiResponse<CsvDataResponse>
        return response.data
      }),
      catchError((e) => {
        console.log(e)
        if (e.status === 404) {
          return Rx.of('NOT_FOUND' as 'NOT_FOUND')
        } else {
          throw e
        }
      })
    )
  }
}

type MagliveApiResponse<T> = {
  status: number
  data: T
}
